import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Lock, Play, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { StepIndustry } from "./steps/StepIndustry";
import { StepBusiness } from "./steps/StepBusiness";
import { StepGoal } from "./steps/StepGoal";
import { StepTimeline } from "./steps/StepTimeline";
import { StepBranding } from "./steps/StepBranding";
import { StepDetails } from "./steps/StepDetails";
import { StepPricingPreference } from "./steps/StepPricingPreference";
import { StepConfirmation } from "./steps/StepConfirmation";
import { StepConfirmationCal } from "./steps/StepConfirmationCal";
import { StepBooked } from "./steps/StepBooked";
import { StepBookedSide } from "./steps/StepBookedSide";
import type { LeadFormData, PlanKey, PricingPreference } from "./types";
import wendelSantosImg from "@/assets/wendel-santos-avatar.webp";

function buildLeadRow(data: LeadFormData) {
  return {
    status: "pending_booking",
    lead_type: "mockup",
    industry: data.industry ?? null,
    industry_other: data.industryOther ?? null,
    business_name: data.businessName ?? null,
    website_status: data.websiteStatus ?? null,
    current_website_url: data.currentWebsiteUrl ?? null,
    inspiration_url: data.inspirationUrl ?? null,
    website_goal: data.websiteGoal ?? null,
    launch_timeline: data.launchTimeline ?? null,
    brand_style: data.brandStyle ?? null,
    full_name: data.fullName ?? null,
    email: data.email ?? null,
    phone_country: data.phoneCountry ?? "us",
    phone_number: data.phoneNumber ?? null,
    instagram_handle: data.instagramHandle?.trim() ? data.instagramHandle.trim() : null,
    decision_makers: data.decisionMakers ?? null,
    website_description: data.websiteDescription ?? null,
    utm_source: data.utm_source ?? null,
    utm_medium: data.utm_medium ?? null,
    utm_campaign: data.utm_campaign ?? null,
    utm_term: data.utm_term ?? null,
    utm_content: data.utm_content ?? null,
    referrer: data.referrer ?? null,
    landing_page: data.landing_page ?? null,
    entry_plan: data.entry_plan ?? null,
  };
}

const TOTAL_STEPS = 9;
const FORM_STEPS = 7;

interface Props {
  initialPlan?: PlanKey;
  onClose?: () => void;
  onStepChange?: (step: number) => void;
}


function planKeyToPreference(plan?: PlanKey | null): PricingPreference | undefined {
  if (plan === "launch" || plan === "growth" || plan === "custom") return plan;
  return undefined;
}

function readUrlTracking() {
  if (typeof window === "undefined") return {} as Record<string, string | null>;
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || null,
    utm_medium: p.get("utm_medium") || null,
    utm_campaign: p.get("utm_campaign") || null,
    utm_term: p.get("utm_term") || null,
    utm_content: p.get("utm_content") || null,
    referrer: document.referrer || null,
    landing_page: window.location.pathname || null,
    entry_plan: p.get("plan") || null,
  };
}

export function LeadFormBody({ initialPlan, onClose, onStepChange }: Props) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<LeadFormData>({});
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    onStepChange?.(step);
  }, [step, onStepChange]);


  useEffect(() => {
    if (!data.websiteStatus) return;
    const el = bodyRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [data.websiteStatus]);

  // On mount: seed tracking + pre-fill pricingPreference from initialPlan or URL ?plan=
  useEffect(() => {
    const tracking = readUrlTracking();
    const urlPlan = planKeyToPreference(tracking.entry_plan as PlanKey | null);
    const preselect = planKeyToPreference(initialPlan) ?? urlPlan;
    setData((d) => ({
      ...d,
      ...tracking,
      entry_plan: initialPlan ?? tracking.entry_plan ?? null,
      pricingPreference: d.pricingPreference ?? preselect,
    }));
  }, [initialPlan]);

  const [leadId, setLeadId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = (patch: Partial<LeadFormData>) =>
    setData((d) => ({ ...d, ...patch }));

  const canContinue = (() => {
    if (step === 1) {
      if (!data.industry) return false;
      if (data.industry === "Other" && !data.industryOther?.trim()) return false;
      return true;
    }
    if (step === 2) {
      if (!data.businessName?.trim()) return false;
      if (!data.websiteStatus) return false;
      if (
        (data.websiteStatus === "needs-replacing" ||
          data.websiteStatus === "terrible") &&
        !data.currentWebsiteUrl?.trim()
      )
        return false;
      return true;
    }
    if (step === 3) {
      const goals = data.websiteGoal ?? [];
      return goals.length >= 1 && goals.length <= 2;
    }
    if (step === 4) return Boolean(data.launchTimeline);
    if (step === 6) {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email?.trim() ?? "");
      return (
        Boolean(data.fullName?.trim()) &&
        emailOk &&
        Boolean(data.phoneNumber?.trim()) &&
        Boolean(data.decisionMakers) &&
        Boolean(data.websiteDescription?.trim())
      );
    }
    if (step === 7) return Boolean(data.pricingPreference);
    return true;
  })();

  const isLast = step === FORM_STEPS;
  const hideFooter = step >= 8;

  const handleBooked = (booking: NonNullable<LeadFormData["booking"]>) => {
    setData((d) => ({ ...d, booking }));
    setStep(9);
    if (leadId) {
      void supabase
        .from("leads")
        .update({
          status: "booked",
          booking_uid: booking.uid ?? null,
          booking_start_time: booking.startTime ?? null,
          booking_end_time: booking.endTime ?? null,
          booking_event_type: booking.eventTypeTitle ?? null,
        })
        .eq("id", leadId)
        .then(({ error }) => {
          if (error) console.error("[leads] booking update failed", error);
        });
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleContinue = async () => {
    if (!canContinue || submitting) return;
    if (step === 6 && !leadId) {
      setSubmitting(true);
      setSubmitError(null);
      const newLeadId = crypto.randomUUID();

      // Upload logo to "logos" bucket first (if provided)
      let logoFileUrl: string | null = null;
      if (data.logoFile) {
        const file = data.logoFile;
        const ext = file.name.split(".").pop() || "bin";
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `${newLeadId}/${Date.now()}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from("logos")
          .upload(path, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type || `image/${ext}`,
          });
        if (uploadError) {
          console.error("[leads] logo upload failed", uploadError);
        } else {
          const { data: pub } = supabase.storage.from("logos").getPublicUrl(path);
          logoFileUrl = pub.publicUrl;
        }
      }

      const { error } = await supabase
        .from("leads")
        .insert({ id: newLeadId, ...buildLeadRow(data), logo_file_url: logoFileUrl });
      setSubmitting(false);
      if (error) {
        console.error("[leads] insert failed", error);
        setSubmitError("Something went wrong saving your details. Please try again.");
        return;
      }
      setLeadId(newLeadId);
    }

    if (step === 7 && leadId && data.pricingPreference) {
      console.log("[leads] pricing update — leadId:", leadId, "pricingPreference:", data.pricingPreference);
      setSubmitting(true);
      const { data: updated, error } = await supabase
        .from("leads")
        .update({ pricing_preference: data.pricingPreference })
        .eq("id", leadId)
        .select();
      setSubmitting(false);
      if (error) {
        console.error("[leads] pricing update failed", error);
      } else {
        console.log("[leads] pricing update ok", updated);
      }
    }
    setStep((s) => s + 1);
  };

  const savePartnerInviteEmail = async (email: string) => {
    const trimmed = email.trim();
    if (!leadId || !trimmed) return;
    const { error } = await supabase
      .from("leads")
      .update({ partner_invite_email: trimmed })
      .eq("id", leadId);
    if (error) console.error("[leads] partner invite update failed", error);
  };

  const advance = () => {
    if (step >= TOTAL_STEPS) return;
    setStep((s) => s + 1);
  };

  const progress = (step / TOTAL_STEPS) * 100;

  const formCard = (
    <div
      className={cn(
        "relative z-10 flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-xl [clip-path:inset(0_round_1rem)] isolate",
        step === 9 ? "max-w-none" : step === 5 ? "max-w-[744px]" : step === 6 || step === 7 ? "max-w-[804px] 2xl:max-w-[932px]" : "max-w-2xl",
        step !== 9 && step !== 8 && "lg:max-h-[calc(100vh-6rem)]",
      )}
    >
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl [clip-path:inset(0_round_1rem)]">
        <div className={cn("absolute -top-10 -right-10 h-40 w-40 rounded-full bg-hero-glow-cool blur-2xl lg:-top-24 lg:-right-24 lg:h-72 lg:w-72 lg:blur-3xl", step === 8 ? "opacity-20" : "opacity-40")} />
        <div className={cn("absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-hero-glow-warm blur-2xl lg:-bottom-24 lg:-left-24 lg:h-72 lg:w-72 lg:blur-3xl", step === 8 ? "opacity-20" : "opacity-40")} />
      </div>

      {/* Progress */}
      <div className="shrink-0 px-6 pt-6 sm:px-8">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-brand-blue transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-2 text-xs font-medium text-gray-950">
          Step {step} of {TOTAL_STEPS}
        </div>
      </div>

      {/* Step body */}
      <div ref={bodyRef} className={cn("min-h-0 flex-1 px-6 pb-4 pt-6 sm:px-8", step !== 9 && step !== 8 && "lg:overflow-y-auto")}>
        {step === 1 && <StepIndustry data={data} onChange={update} onAdvance={advance} />}
        {step === 2 && <StepBusiness data={data} onChange={update} />}
        {step === 3 && <StepGoal data={data} onChange={update} />}
        {step === 4 && <StepTimeline data={data} onChange={update} onAdvance={advance} />}
        {step === 5 && <StepBranding data={data} onChange={update} />}
        {step === 6 && <StepDetails data={data} onChange={update} />}
        {step === 7 && <StepPricingPreference data={data} onChange={update} />}
        {step === 8 && <StepConfirmation data={data} onBooked={handleBooked} />}
        {step === 9 && <StepBooked data={data} />}
      </div>

      {/* Footer */}
      {!hideFooter && (
        <div className="border-t border-border bg-background/80 px-6 py-4 backdrop-blur sm:px-8">
          <div className="flex items-center justify-between gap-3">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            ) : (
              <span />
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue || submitting}
              className={cn(
                "group inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-brand-blue-foreground transition-all",
                "hover:bg-brand-blue-hover active:scale-[0.98]",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand-blue",
              )}
            >
              {submitting ? "Saving…" : isLast ? "Get My Free Mockup" : "Continue"}
              {isLast ? (
                <Sparkles className="h-4 w-4" />
              ) : (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>
          </div>

          {submitError && (
            <p className="mt-3 text-center text-xs text-destructive">{submitError}</p>
          )}
        </div>
      )}
    </div>
  );

  const trustLine = (
    <p className="mt-[22px] flex items-center gap-2 px-1 text-xs text-muted-foreground">
      <Lock className="h-3.5 w-3.5" />
      No payment needed. No obligation. Your mockup in under 12 hours.
    </p>
  );


  if (step === 8) {
    return (
      <div className="w-full max-w-[1500px] 2xl:mt-5">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)]">
          <div className="flex flex-col gap-5">
            {formCard}
            <div className="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">

              <img
                src={wendelSantosImg}
                alt="Wendel Santos"
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
              <div className="min-w-0">
                <div className="text-base font-semibold text-foreground">Hey👋 I'm Wendel Santos</div>
                <p className="text-sm text-muted-foreground">
                  I'll personally run your mockup call and walk you through the plan.
                </p>
              </div>
            </div>
            <div className="h-px w-full bg-border" />
            {trustLine}
          </div>
          <StepConfirmationCal data={data} onBooked={handleBooked} />
        </div>
      </div>
    );
  }

  if (step === 9) {
    return (
      <div className="w-full max-w-[1500px]">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            {formCard}
            {/* Desktop-only button */}
            <div className="relative z-10 hidden flex-col items-end gap-3 lg:flex">
              <button
                type="button"
                onClick={handleClose}
                className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-brand-blue-foreground transition-all hover:bg-brand-blue-hover active:scale-[0.98]"
              >
                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                Back to Site
              </button>
              <p className="text-xs text-brand-blue/80">
                Need to reschedule? Use the link in your confirmation email.
              </p>
            </div>
          </div>
          <StepBookedSide
            data={data}
            onChange={update}
            onSavePartnerInvite={savePartnerInviteEmail}
          />
          {/* Mobile-only button at bottom, centered */}
          <div className="relative z-10 flex flex-col items-center gap-3 lg:hidden">
            <button
              type="button"
              onClick={handleClose}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-6 py-3 text-sm font-semibold text-brand-blue-foreground transition-all hover:bg-brand-blue-hover active:scale-[0.98]"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              Back to Site
            </button>
            <p className="text-center text-xs text-brand-blue/80">
              Need to reschedule? Use the link in your confirmation email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-stretch">
      {formCard}
      {trustLine}
    </div>
  );

}

